import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, Upload, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 30 * 1024 * 1024; // must match PRINT_MASTER_MAX_BYTES on the backend

/**
 * Admin panel: shows which print sizes this artwork can be printed at sharply, and lets the admin
 * attach a higher-resolution "print master" (e.g. an approved AI-upscaled file). The print master is
 * used ONLY for prints; the gallery images are not changed.
 */
const PrintQualityPanel = ({ artwork, token, onChanged }) => {
  const [quality, setQuality] = useState(null); // { india: {...}, international: {...} }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!artwork?.id) return;
    try {
      const [ind, intl] = await Promise.all(['india', 'international'].map(region =>
        axios.get(`${API}/print-pricing`, { params: { region, artwork_id: artwork.id } })));
      // Count only what customers are offered: sizes in the artwork's orientation, plus square
      // (same rule as getRecommendedSizes in PrintConfigurator).
      const w = artwork.image_width, h = artwork.image_height;
      const r = w && h ? w / h : 0;
      const orient = !r ? 'portrait' : (r >= 0.9 && r <= 1.1 ? 'square' : (r > 1.1 ? 'landscape' : 'portrait'));
      const summarise = (p) => {
        const entries = Object.entries(p.data.sizes || {})
          .filter(([, v]) => v.orientation === orient || v.orientation === 'square');
        const sharp = entries.filter(([, v]) => v.available !== false);
        const largest = sharp.sort((a, b) => (b[1].width * b[1].height) - (a[1].width * a[1].height))[0];
        return { sharp: sharp.length, total: entries.length, largest: largest ? largest[1].label : null,
                 minDpi: p.data.min_print_dpi, source: p.data.print_source };
      };
      setQuality({ india: summarise(ind), international: summarise(intl) });
    } catch {
      setQuality(null);
    }
  }, [artwork?.id, artwork?.image_width, artwork?.image_height]);

  // Reload after a print file change or after the main image is edited (image_version changes)
  useEffect(() => { load(); }, [load, artwork?.print_master_width, artwork?.image_version]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('File is over 30 MB. Save it as JPG (quality 90-95) and try again.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/artworks/${artwork.id}/print-master`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Print file saved (${res.data.width}x${res.data.height}px)`);
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Stop using the high-resolution print file? Prints will use the original image again.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/artworks/${artwork.id}/print-master`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Print file removed');
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove');
    } finally { setBusy(false); }
  };

  const hasMaster = !!artwork?.print_master_width;
  const line = (label, q) => q && (
    <p className="text-xs">
      <span className="text-muted-foreground">{label}:</span>{' '}
      {q.sharp === 0
        ? <span className="text-red-600 font-medium">no sharp size (prints hidden)</span>
        : <span className={q.sharp < q.total ? 'text-amber-700' : 'text-green-700'}>
            {q.sharp} of {q.total} sizes, up to {q.largest}
          </span>}
    </p>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="print-quality-panel">
      <h3 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-2">
        <Printer size={13} /> Print quality
      </h3>
      <p className="text-xs text-muted-foreground">
        Print file: {hasMaster
          ? <><span className="text-foreground font-medium">high-res master {artwork.print_master_width} x {artwork.print_master_height}px</span></>
          : <>original image {artwork?.image_width} x {artwork?.image_height}px</>}
      </p>
      {line('India', quality?.india)}
      {line('International', quality?.international)}
      {quality?.india?.minDpi && (
        <p className="text-[11px] text-muted-foreground">Sizes below {quality.india.minDpi} DPI are hidden from customers so prints stay sharp.</p>
      )}

      <label className={`flex items-center justify-center gap-2 w-full rounded-md border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:border-accent ${busy ? 'opacity-50 pointer-events-none' : ''}`}
        data-testid="print-master-upload">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {hasMaster ? 'Replace high-res print file' : 'Upload high-res print file'}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
      </label>
      <p className="text-[11px] text-muted-foreground -mt-1">
        Same picture, uncropped, just larger (e.g. a 4x upscale or a new scan). JPG/PNG/WebP, up to 30 MB and 8000px.
        Used only for prints; the gallery image doesn't change. Replacing or editing the main image removes it.
      </p>
      {hasMaster && (
        <Button variant="outline" size="sm" onClick={remove} disabled={busy}
          className="w-full text-red-600 border-red-200 hover:bg-red-50" data-testid="print-master-remove">
          <Trash2 size={13} className="mr-2" /> Remove print file
        </Button>
      )}
    </div>
  );
};

export default PrintQualityPanel;
